import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CHANNEL_PALETTE } from './channel-palette';

export type ChannelChipChannel = '4G' | 'BLE' | 'LORA';
export type ChannelChipState = 'live' | 'seen' | 'never';

/** Chip colours come from the shared {@link CHANNEL_PALETTE} (4G violet, BLE emerald, LoRa amber). */
const LIVE_CLASS: Record<ChannelChipChannel, string> = {
  '4G': 'border-transparent ' + CHANNEL_PALETTE['4G'].pill,
  BLE: 'border-transparent ' + CHANNEL_PALETTE.BLE.pill,
  LORA: 'border-transparent ' + CHANNEL_PALETTE.LORA.pill,
};
const LIVE_DOT: Record<ChannelChipChannel, string> = {
  '4G': CHANNEL_PALETTE['4G'].dot,
  BLE: CHANNEL_PALETTE.BLE.dot,
  LORA: CHANNEL_PALETTE.LORA.dot,
};
const LABEL: Record<ChannelChipChannel, string> = {
  '4G': CHANNEL_PALETTE['4G'].label,
  BLE: CHANNEL_PALETTE.BLE.label,
  LORA: CHANNEL_PALETTE.LORA.label,
};

/**
 * One radio channel of a tracker (4G, BLE, LoRa) and its presence: live (tinted, pulsing
 * dot), seen (outlined, hollow dot, "2 h" value) or never (dashed, muted). Same anatomy as
 * {@link WhChipComponent} so it sits in a {@link WhStatusStripComponent} row. Rendered for
 * all three channels in a fixed order so a missing channel is as visible as a live one.
 */
@Component({
  selector: 'lib-channel-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  template: `
    <span
      class="inline-flex items-center gap-1.5 whitespace-nowrap rounded border px-2.5 py-[3px] text-[11px] font-bold uppercase leading-[1.3] tracking-[0.02em]"
      [class]="chipClass()"
      [title]="title() ?? ''">
      @if (state() === 'live') {
        <!-- One ping per received frame: the ring is keyed on pulseKey (the channel's lastSeen),
             which the live merges bump on every incoming position — same idea as the map marker. -->
        <span class="relative inline-flex h-[7px] w-[7px] shrink-0 items-center justify-center">
          @for (k of [pulseKey()]; track k) {
            <span
              class="animate-ping-once absolute inline-flex h-full w-full rounded-full opacity-75"
              [class]="pingClass()"></span>
          }
          <span class="relative inline-block h-[7px] w-[7px] rounded-full" [class]="solidDotClass()"></span>
        </span>
      } @else {
        <span class="inline-block h-[7px] w-[7px] shrink-0 rounded-full" [class]="dotClass()"></span>
      }
      <span>{{ label() }}</span>
      @if (value(); as v) {
        <span class="font-medium normal-case tracking-normal opacity-85">{{ v }}</span>
      }
      @if (bars() !== null) {
        <span class="ml-0.5 inline-flex h-[10px] items-end gap-[1.5px]" aria-hidden="true">
          @for (h of BAR_HEIGHTS; track $index) {
            <span
              class="w-[2.5px] rounded-[1px] bg-current"
              [style.height.px]="h"
              [class.opacity-30]="$index >= bars()!"></span>
          }
        </span>
      }
    </span>
  `,
})
export class ChannelChipComponent {
  readonly channel = input.required<ChannelChipChannel>();
  readonly state = input<ChannelChipState>('never');
  /** Trailing detail in lowercase: "2 h", "13/15", "ch 3 · slot 7". */
  readonly value = input<string | null | undefined>(undefined);
  /** Signal bars 0–4, LoRa only. Null hides the bars. */
  readonly bars = input<number | null>(null);
  readonly title = input<string | undefined>(undefined);
  /**
   * Changes on each received frame (the channel's lastSeen in epoch ms). Re-creates the ping ring
   * so the live dot emits a pulse per position, not once. Null still pings once on first render.
   */
  readonly pulseKey = input<number | null>(null);

  protected readonly BAR_HEIGHTS = [3, 5, 7, 10];
  protected readonly pingClass = computed(() => CHANNEL_PALETTE[this.channel()].ping);
  protected readonly solidDotClass = computed(() => CHANNEL_PALETTE[this.channel()].marker);
  protected readonly label = computed(() => LABEL[this.channel()]);

  protected readonly chipClass = computed(() => {
    switch (this.state()) {
      case 'live':
        return LIVE_CLASS[this.channel()];
      case 'seen':
        return 'border-gray-300 bg-white text-gray-500 dark:border-slate-600 dark:bg-transparent dark:text-slate-400';
      default:
        return 'border-dashed border-gray-300 bg-white text-gray-400 dark:border-slate-600 dark:bg-transparent dark:text-slate-500';
    }
  });

  protected readonly dotClass = computed(() => {
    switch (this.state()) {
      case 'live':
        return LIVE_DOT[this.channel()] + ' animate-ping-once';
      case 'seen':
        return 'border-[1.5px] border-gray-400 bg-transparent dark:border-slate-500';
      default:
        return 'border-[1.5px] border-dashed border-gray-400 bg-transparent dark:border-slate-500';
    }
  });
}
